import { Injectable } from "@angular/core";
import { Http, Headers, Response, RequestOptions } from "@angular/http";
import { Subject } from "rxjs/Subject";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import "rxjs/add/operator/map";
import { HttpWrapperService } from "./http.wrapper.service";
import { SessionManagerService } from "./SessionManager/session-manager.service";
import { Observable } from "rxjs/Observable";
import { GenericUser } from "../_models";
import {
  LoginResult,
  SecondFactorChallenge,
  SecondFactorChannel,
} from "../_models/second-factor";

@Injectable()
export class AuthenticationService {
  public currentUser = new Subject<any>();
  public siteId = new Subject<any>();

  private static readonly DEFAULT_REMEMBER_DEVICE_DAYS = 15;
  // Falls back to the default until the login response supplies `trustedDeviceDays`
  private rememberDeviceDaysSubject = new BehaviorSubject<number>(
    AuthenticationService.DEFAULT_REMEMBER_DEVICE_DAYS
  );
  /** How many days a device is remembered after a successful 2FA */
  public readonly rememberDeviceDays$: Observable<number> =
    this.rememberDeviceDaysSubject.asObservable();

  constructor(
    private http: HttpWrapperService,
    private sessionManager: SessionManagerService
  ) {}

  public get twoFactorLogin(): boolean {
    return this.sessionManager.features.twoFactorLogin;
  }

  /** Routes by the `twoFactorLogin` feature flag */
  public login(username: string, password: string): Observable<LoginResult> {
    if (!this.twoFactorLogin) {
      return this.legacyLogin(username, password).map(
        (): LoginResult => ({ status: "LOGGED_IN" })
      );
    }
    return this.loginWithTwoFactor(username, password);
  }

  /** The login as it is on `main`, used while the `twoFactorLogin` flag is off */
  public legacyLogin(username: string, password: string) {
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    const options = new RequestOptions({ headers });
    return this.http
      .post("login", { username, password }, options)
      .map((response: Response) => {
        // login successful if there's a jwt token in the response
        if (response.json()) {
          const user = response.json();

          if (user && user.token) {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem("currentUser", JSON.stringify(user));
            localStorage.setItem("allowShowPopup", "yes");
            this.sessionManager.setUser(user as GenericUser);
            this.currentUser.next(user);
          }
        }
      });
  }

  public loginWithTwoFactor(
    username: string,
    password: string
  ): Observable<LoginResult> {
    return this.http
      .post("login", { username, password }, this.jsonOptions())
      .map((response: Response) => this.toLoginResult(response));
  }

  /** The second step of the login: the code that was sent to the user, and whether to skip it next time */
  public verifyCode(
    code: string,
    rememberDevice: boolean
  ): Observable<LoginResult> {
    return this.http
      .post("login/verify", { code, rememberDevice }, this.jsonOptions())
      .map((response: Response) => this.toLoginResult(response));
  }

  /** Asks for a new code, by the channel the user chose or the one that was used before */
  public resendCode(
    channel?: SecondFactorChannel
  ): Observable<SecondFactorChallenge> {
    return this.http
      .post("login/resend", { channel }, this.jsonOptions())
      .map((response: Response) => this.toChallenge(response.json()));
  }

  public setSiteId(siteId) {
    this.siteId.next(siteId);
  }

  public logout() {
    //     	alert('logout');
    localStorage.removeItem("currentUser");
    localStorage.removeItem("cart");
    localStorage.removeItem("texts");
    localStorage.removeItem("nests");
    localStorage.removeItem("allNests");
    localStorage.removeItem("layers");
    localStorage.removeItem("docs");
    localStorage.removeItem("sitecptch");
    localStorage.removeItem("countries");
    localStorage.removeItem("generalTexts");
    localStorage.removeItem("allowShowPopup");

    this.currentUser.next(null);
    this.siteId.next(null);
    this.sessionManager.setUser(null);
    this.http
      .post("/logout", JSON.stringify({}), {
        headers: new Headers({
          "Content-Type": "application/json",
        }),
      })
      .map((res) => res.json())
      .subscribe();
  }

  private jsonOptions() {
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    return new RequestOptions({ headers });
  }

  /** The user is logged in only when the server says so: a code that is still needed keeps nothing in the browser */
  private toLoginResult(response: Response): LoginResult {
    const body = response.json();
    if (body && body.twoFactorRequired) {
      return {
        status: "SECOND_FACTOR_REQUIRED",
        challenge: this.toChallenge(body),
      };
    }
    // login successful if there's a jwt token in the response
    if (body && body.token) {
      // store user details and jwt token in local storage to keep user logged in between page refreshes
      localStorage.setItem("currentUser", JSON.stringify(body));
      localStorage.setItem("allowShowPopup", "yes");
      this.sessionManager.setUser(body as GenericUser);
      this.currentUser.next(body);
    }
    return { status: "LOGGED_IN" };
  }

  private toChallenge(body): SecondFactorChallenge {
    const days = Number(body.trustedDeviceDays);
    if (days > 0) {
      this.rememberDeviceDaysSubject.next(days);
    }
    return {
      channel: body.channel,
      destination: body.destination,
      canUseEmail: Boolean(body.canUseEmail),
      expiresInSeconds: body.expiresInSeconds,
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/authentication.service.ts